// Named import: the SDK is CJS-only and Vite's default-import interop hands back the exports object.
import {
	Intercom,
	shutdown as shutdownIntercom,
} from "@intercom/messenger-js-sdk";
import {
	createFileRoute,
	Navigate,
	Outlet,
	useRouterState,
} from "@tanstack/react-router";
import { Button, Flex, Input, Space, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useArtemisAuth } from "@/auth/artemis-auth-provider";
import { useMe } from "@/common/artemis-operations/members";
import {
	useClaimOrganization,
	useLookupOrgByDomain,
} from "@/common/artemis-operations/organization";
import { OrganizationLookupBySlugDocument } from "@/common/artemis-sdk/graphql";
import { ERROR_NETWORK_DESC } from "@/common/messages";
import { useGqlClient } from "@/common/use-gql-client";
import { AuthPageLayout } from "@/components/auth-layout";
import { FullSpin } from "@/components/base";
import WorkspaceLayout from "@/components/layout/workspace/workspace-layout";
import { RealtimeProvider } from "@/contexts/realtime-context";

export const Route = createFileRoute("/_authenticated")({
	component: AuthenticatedLayout,
});

function domainFromEmail(email: string): string {
	return email.split("@")[1]?.toLowerCase() ?? "";
}

function suggestOrgName(domain: string): string {
	return domain.split(".")[0]
		? domain.split(".")[0].charAt(0).toUpperCase() +
				domain.split(".")[0].slice(1)
		: "";
}

function suggestSlug(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, 48);
}

type SlugStatus = "idle" | "checking" | "available" | "taken";

function OnboardingFlow() {
	const { logout, user } = useArtemisAuth();
	const gqlFetch = useGqlClient();
	const claim = useClaimOrganization();
	const email = user?.email ?? "";
	const domain = domainFromEmail(email);
	const lookup = useLookupOrgByDomain(domain);
	const [orgName, setOrgName] = useState("");
	const [orgSlug, setOrgSlug] = useState("");
	const [slugEdited, setSlugEdited] = useState(false);
	const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");

	const existingOrg = lookup.data?.organizationLookupByDomain;

	useEffect(() => {
		if (!slugEdited) setOrgSlug(suggestSlug(orgName));
	}, [orgName, slugEdited]);

	useEffect(() => {
		console.log("slug idle", orgSlug);
		setSlugStatus("idle");
	}, [orgSlug]);

	const checkAvailability = useCallback(async () => {
		if (orgSlug.length < 3) return;
		setSlugStatus("checking");
		try {
			const result = await gqlFetch(OrganizationLookupBySlugDocument, {
				slug: orgSlug,
			});
			setSlugStatus(result.organizationLookupBySlug ? "taken" : "available");
		} catch {
			setSlugStatus("idle");
		}
	}, [gqlFetch, orgSlug]);

	if (claim.isPending || lookup.isPending) {
		return (
			<div style={{ height: "100vh" }}>
				<FullSpin size="large" description="Setting up your account…" />
			</div>
		);
	}

	if (claim.isError) {
		const message =
			claim.error instanceof Error ? claim.error.message : ERROR_NETWORK_DESC;
		return (
			<AuthPageLayout>
				<Flex vertical align="center" gap={16}>
					<Flex vertical align="center" gap={6}>
						<Typography.Title
							level={3}
							style={{ margin: 0, textAlign: "center" }}
						>
							Something went wrong
						</Typography.Title>
						<Typography.Text type="secondary" style={{ textAlign: "center" }}>
							{message}
						</Typography.Text>
					</Flex>
					<Button block onClick={() => logout()}>
						Sign out
					</Button>
				</Flex>
			</AuthPageLayout>
		);
	}

	if (existingOrg) {
		return (
			<AuthPageLayout>
				<Flex vertical align="center" gap={24}>
					<Space vertical align="center">
						<Typography.Title
							level={3}
							style={{ margin: 0, textAlign: "center" }}
						>
							Join {existingOrg.orgName}
						</Typography.Title>
						<Typography.Text type="secondary" style={{ textAlign: "center" }}>
							Your company already uses StratoNext.
						</Typography.Text>
						<Typography.Text type="secondary" style={{ textAlign: "center" }}>
							Click below to join.
						</Typography.Text>
					</Space>
					<Button
						type="primary"
						size="large"
						block
						loading={claim.isPending}
						onClick={() => claim.mutate({ name: "", slug: "" })}
						style={{ height: 48, fontSize: 15, borderRadius: 8 }}
					>
						Join organization
					</Button>
					<Typography.Link
						onClick={() => logout()}
						style={{ color: "inherit", opacity: 0.5, fontSize: 13 }}
					>
						Sign out
					</Typography.Link>
				</Flex>
			</AuthPageLayout>
		);
	}

	return (
		<AuthPageLayout>
			<Flex vertical gap={16}>
				<Flex vertical align="center" gap={6} style={{ marginBottom: 8 }}>
					<Typography.Title
						level={3}
						style={{ margin: 0, textAlign: "center" }}
					>
						Set up your organization
					</Typography.Title>
					<Typography.Text type="secondary" style={{ textAlign: "center" }}>
						Choose a name and URL handle for your organization.
					</Typography.Text>
				</Flex>

				<Input
					size="large"
					placeholder={suggestOrgName(domain) || "My Organization"}
					value={orgName}
					onChange={(e) => setOrgName(e.target.value)}
				/>

				<Flex gap={8}>
					<Input
						size="large"
						prefix="@"
						placeholder="slug"
						value={orgSlug}
						onChange={(e) => {
							setOrgSlug(
								e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
							);
							setSlugEdited(true);
						}}
						status={slugStatus === "taken" ? "error" : undefined}
						style={{ flex: 1 }}
					/>
					<Button
						size="large"
						onClick={checkAvailability}
						loading={slugStatus === "checking"}
						disabled={orgSlug.length < 3}
					>
						Check
					</Button>
				</Flex>

				<Typography.Text type="secondary" style={{ fontSize: 12 }}>
					<Space>
						Slug availability:
						{slugStatus !== "available" && slugStatus !== "taken" && (
							<Typography.Text type="warning" style={{ fontSize: 12 }}>
								...
							</Typography.Text>
						)}
						{slugStatus === "available" && (
							<Typography.Text type="success" style={{ fontSize: 12 }}>
								Available
							</Typography.Text>
						)}
						{slugStatus === "taken" && (
							<Typography.Text type="danger" style={{ fontSize: 12 }}>
								Already taken. Choose another.
							</Typography.Text>
						)}
					</Space>
				</Typography.Text>

				<Button
					type="primary"
					size="large"
					block
					disabled={
						!orgName.trim() || orgSlug.length < 3 || slugStatus !== "available"
					}
					loading={claim.isPending}
					onClick={() =>
						claim.mutate({ name: orgName.trim(), slug: orgSlug.trim() })
					}
					style={{ height: 48, fontSize: 15, borderRadius: 8, marginTop: 4 }}
				>
					Create organization
				</Button>

				<Typography.Link
					onClick={() => logout()}
					style={{
						textAlign: "center",
						color: "inherit",
						opacity: 0.5,
						fontSize: 13,
					}}
				>
					Sign out
				</Typography.Link>
			</Flex>
		</AuthPageLayout>
	);
}

function AuthenticatedLayout() {
	const { isLoading, isAuthenticated } = useArtemisAuth();
	const me = useMe({ enabled: isAuthenticated });
	const leafRouteId = useRouterState({
		select: (s) => s.matches.at(-1)?.routeId,
	});

	// Boot the Intercom messenger for the signed-in user; shut it down on logout so the next user on
	// this browser doesn't inherit the conversation history.
	useEffect(() => {
		const user = me.data;
		if (!user) return;
		Intercom({
			app_id: "c92j24lv",
			// Lift the launcher above the footer buttons of our floating drawers (CustomDrawer sits
			// 16px off the viewport bottom, so its action row ends ~80px up); Intercom's default
			// 20px padding puts the bubble right on top of them.
			vertical_padding: 96,
			user_id: user.id,
			name: user.displayName,
			email: user.email,
			created_at: Math.floor(Date.parse(user.createdAt as string) / 1000),
		});
		return () => shutdownIntercom();
	}, [me.data]);

	if (isLoading || (isAuthenticated && me.isPending))
		return (
			<div style={{ height: "100vh" }}>
				<FullSpin />
			</div>
		);
	if (!isAuthenticated) {
		// Remember the deep link across the login round-trip; /login restores it once authenticated.
		sessionStorage.setItem(
			"returnTo",
			window.location.pathname + window.location.search,
		);
		return <Navigate to="/login" replace />;
	}
	if (!me.data && !me.isPending) return <OnboardingFlow />;

	// The post-login redirector and the workspace picker render their own bare shell (matched by
	// route id, not pathname, so the admin "/$orgSlug/admin/workspaces" page is unaffected). Every other
	// authenticated page (workspace pages and the org-admin pages) shares this single, persistent
	// WorkspaceLayout — mounted once at the common ancestor — so navigating between a workspace and
	// the org-admin area swaps only the content, not the whole layout.
	const isBare =
		leafRouteId === "/_authenticated/home" ||
		leafRouteId === "/_authenticated/aws-mktp" ||
		leafRouteId === "/_authenticated/billing/$outcome" ||
		leafRouteId === "/_authenticated/$orgSlug/workspaces/";
	if (isBare)
		return (
			<RealtimeProvider>
				<Outlet />
			</RealtimeProvider>
		);

	return (
		<RealtimeProvider>
			<WorkspaceLayout>
				<Outlet />
			</WorkspaceLayout>
		</RealtimeProvider>
	);
}
